import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import api from '../api/api';
import 'react-datepicker/dist/react-datepicker.css';
import 'leaflet/dist/leaflet.css';
import './publicListings.css';

// Leaflet's default marker icon URLs don't resolve correctly under webpack bundling.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const DEFAULT_MAP_CENTER = [39.8283, -98.5795];
const DEFAULT_MAP_ZOOM = 4;
const MAP_MAX_AUTO_ZOOM = 14;

const MapViewport = ({ properties }) => {
    const map = useMap();

    useEffect(() => {
        if (properties.length === 0) {
            map.setView(DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM);
            return;
        }

        const points = properties.map((property) => [Number(property.latitude), Number(property.longitude)]);
        if (points.length === 1) {
            map.setView(points[0], MAP_MAX_AUTO_ZOOM);
            return;
        }

        map.fitBounds(points, { padding: [32, 32], maxZoom: MAP_MAX_AUTO_ZOOM });
    }, [map, properties]);

    return null;
};

const toLocalDateKey = (value) => {
    if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const PublicListingsPage = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [stayRange, setStayRange] = useState([null, null]);
    const [stayStartDate, stayEndDate] = stayRange;

    const loadProperties = async (startDate, endDate) => {
        setLoading(true);
        setError('');
        try {
            const query = startDate && endDate
                ? `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`
                : '';
            const response = await api.get(`/public/properties${query}`);
            setProperties(Array.isArray(response?.data) ? response.data : []);
        } catch (e) {
            setError('Unable to load available properties right now.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProperties();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onStayRangeChange = (dates) => {
        setStayRange(dates);
        const [start, end] = dates;
        if (start && end) {
            loadProperties(toLocalDateKey(start), toLocalDateKey(end));
        }
    };

    const clearStayRange = () => {
        setStayRange([null, null]);
        loadProperties();
    };

    const handleClick = (property) => {
        if (property.bookingSlug) {
            navigate(`/book/${property.bookingSlug}`);
        }
    };

    const mappableProperties = useMemo(
        () => properties.filter((p) => Number.isFinite(Number(p.latitude)) && Number.isFinite(Number(p.longitude)) && p.latitude !== null && p.longitude !== null),
        [properties]
    );

    return (
        <div className="public-listings-page">
            <div className="public-listings-hero">
                <h1>Find Your Perfect Stay</h1>
                <p>Browse available properties and reserve your dates directly.</p>
            </div>

            <div className="public-listings-filter-bar">
                <div className="public-listings-filter-field">
                    <span>Stay Dates</span>
                    <DatePicker
                        selectsRange
                        startDate={stayStartDate}
                        endDate={stayEndDate}
                        onChange={onStayRangeChange}
                        minDate={new Date()}
                        placeholderText="Select check-in and check-out"
                        isClearable={false}
                        className="public-listings-date-input"
                    />
                </div>
                {stayStartDate && stayEndDate && (
                    <button type="button" className="btn-secondary public-listings-clear-dates" onClick={clearStayRange}>
                        Clear Dates
                    </button>
                )}
            </div>
            {stayStartDate && stayEndDate && (
                <p className="public-listings-filter-note">Showing properties available from {toLocalDateKey(stayStartDate)} to {toLocalDateKey(stayEndDate)}.</p>
            )}

            {error && <div className="public-listings-error">{error}</div>}

            {mappableProperties.length > 0 && (
                <div className="public-listings-map-wrap">
                    <MapContainer center={DEFAULT_MAP_CENTER} zoom={DEFAULT_MAP_ZOOM} scrollWheelZoom={false} className="public-listings-map">
                        <MapViewport properties={mappableProperties} />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {mappableProperties.map((property) => (
                            <Marker key={property.id} position={[Number(property.latitude), Number(property.longitude)]}>
                                <Popup>
                                    <strong>{property.name}</strong>
                                    <div>{property.city}, {property.state}</div>
                                    {property.bookingSlug ? (
                                        <button type="button" className="public-listings-map-popup-btn" onClick={() => handleClick(property)}>
                                            View &amp; Book →
                                        </button>
                                    ) : (
                                        <span>Booking coming soon</span>
                                    )}
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
            )}

            {loading ? (
                <div className="public-listings-loading">Loading available properties...</div>
            ) : properties.length === 0 ? (
                <div className="public-listings-empty">
                    {stayStartDate && stayEndDate
                        ? 'No properties are available for the selected dates. Try a different date range.'
                        : 'No properties are available for booking right now. Check back soon.'}
                </div>
            ) : (
                <ul className="public-listings-grid">
                    {properties.map((property) => (
                        <li key={property.id}>
                            <button
                                className={`public-listing-card ${!property.bookingSlug ? 'public-listing-card-no-booking' : ''}`}
                                onClick={() => handleClick(property)}
                                disabled={!property.bookingSlug}
                            >
                                <div className="public-listing-media" aria-hidden="true">
                                    {property.heroImageUrl ? (
                                        <img src={property.heroImageUrl} alt="" />
                                    ) : (
                                        <div className="public-listing-media-fallback">
                                            {String(property.name || '').slice(0, 2).toUpperCase() || 'PR'}
                                        </div>
                                    )}
                                </div>
                                <div className="public-listing-header">
                                    <div className="public-listing-identity">
                                        <h3>{property.name}</h3>
                                        <span>{property.city}, {property.state}</span>
                                    </div>
                                </div>
                                {property.neighborhood && (
                                    <div className="public-listing-neighborhood">Neighborhood: {property.neighborhood}</div>
                                )}
                                <div className="public-listing-details">
                                    {property.bedrooms && (
                                        <span className="public-listing-pill">{property.bedrooms} {Number(property.bedrooms) === 1 ? 'Bed' : 'Beds'}</span>
                                    )}
                                    {property.bathrooms && (
                                        <span className="public-listing-pill">{property.bathrooms} {Number(property.bathrooms) === 1 ? 'Bath' : 'Baths'}</span>
                                    )}
                                </div>
                                {property.bookingSlug ? (
                                    <span className="public-listing-cta">View &amp; Book →</span>
                                ) : (
                                    <span className="public-listing-cta public-listing-cta-soon">Booking coming soon</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default PublicListingsPage;
